import { useEffect, useRef } from 'react';
import { apiFetch } from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import { isUserInternalForOrg } from '../lib/roles';

/**
 * PushSubscriber — invisible component that subscribes the user to Web Push.
 * Renders nothing. Placed once in the layout.
 * Comptes INTERNES seulement (décision du 10/10) : le serveur refuse l'abonnement d'un client ou d'un prestataire
 * (403) et ne leur envoie rien ; sans cette garde, ils verraient la demande d'autorisation du navigateur pour rien.
 * Compte RÉEL (l'abonnement est le sien), rôle racine : la règle de `push_svc.destinataires_permis`.
 */
export default function PushSubscriber() {
  const { user } = useAuth();
  const interne = isUserInternalForOrg(user);
  const done = useRef(false);

  useEffect(() => {
    if (done.current || !interne) return;
    done.current = true;
    subscribeIfNeeded();
  }, [interne]);

  return null;
}

export function memeCle(a, b) {
  if (!a || !b) return false;
  const x = new Uint8Array(a);
  const y = new Uint8Array(b);
  if (x.length !== y.length) return false;
  for (let i = 0; i < x.length; i += 1) if (x[i] !== y[i]) return false;
  return true;
}

export async function subscribeIfNeeded() {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
  try {
    const reg = await navigator.serviceWorker.ready;
    const res = await fetch('/api/push/vapid-key');
    const data = await res.json();
    if (!data.ok || !data.publicKey) return;
    const cle = urlBase64ToUint8Array(data.publicKey);
    const existing = await reg.pushManager.getSubscription();
    if (existing) {
      if (memeCle(existing.options && existing.options.applicationServerKey, cle)) {
        await sendSubscription(existing);
        return;
      }
      // Abonnement lié à une AUTRE clé (paire VAPID régénérée, 2026-10) : le garder = plus jamais rien reçu.
      try {
        await apiFetch('/api/push/unsubscribe', { method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ endpoint: existing.endpoint }) });
      } catch (e) { /* le serveur l'effacera au premier refus du service de push */ }
      await existing.unsubscribe();
    }
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return;
    const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: cle });
    await sendSubscription(sub);
  } catch (e) {
    // Silent fail — push is optional
  }
}

async function sendSubscription(sub) {
  try {
    await apiFetch('/api/push/subscribe', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ subscription: sub.toJSON() }),
    });
  } catch (e) {
    // Ignore
  }
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}
